import {
    BRANCH_BASED_ON,
    DECISION_FIELDS_BY_PATH,
    DECISION_FIELD_GROUPS,
    DECISION_OUTCOME_CONDITIONS,
    FIELD_GROUPS,
    FORM_FIELD_TYPE_CONFIG,
    NEW_BRANCH,
    OPERATORS_BY_VALUE,
    PARENT_OUTPUT_FIELDS,
    PREVIOUS_OUTCOME_FIELD,
} from './constants';

import { NODE_TYPES } from '../../constants';
import startCase from 'lodash/startCase';

// Custom form fields live under requestDetails, keyed by their inputName
// (e.g. "input.requestDetails.o-My custom field").
const CUSTOM_FIELD_PATH_PREFIX = 'input.requestDetails.';

// Custom form field inputNames carry an "o-" prefix (see FormDetails.reducer's addField).
const CUSTOM_FIELD_NAME_PREFIX = 'o-';

// --- Field group sorting ---

// Deferred sort — call once at render time so translate() has loaded
let _sortedFieldGroups = null;
export const getSortedFieldGroups = translate => {
    if (_sortedFieldGroups) {
        return _sortedFieldGroups;
    }
    _sortedFieldGroups = [...DECISION_FIELD_GROUPS]
        .map(g => ({
            ...g,
            fields: [...g.fields].sort((a, b) =>
                translate(a.label).localeCompare(translate(b.label))
            ),
        }))
        .sort((a, b) => translate(a.label).localeCompare(translate(b.label)));
    return _sortedFieldGroups;
};

// --- Path helpers ---

// Detect custom form-field paths — a requestDetails path that isn't one of the
// standard request fields (e.g. the standard requestor email).
export const isCustomFieldPath = path => {
    if (!path?.startsWith(CUSTOM_FIELD_PATH_PREFIX)) return false;
    return !DECISION_FIELDS_BY_PATH[path];
};

// The custom field's inputName, taken from the path segment after the prefix.
export const getFieldIdFromPath = path => path?.slice(CUSTOM_FIELD_PATH_PREFIX.length);
export const isParentFieldPath = path => path?.startsWith('parents.');
export const getParentNodeIdFromPath = path => path?.split('.')?.[1];

export const getCustomFieldLabelFromPath = path => {
    if (!isCustomFieldPath(path)) {
        return null;
    }
    const inputName = getFieldIdFromPath(path);
    const name = inputName.startsWith(CUSTOM_FIELD_NAME_PREFIX)
        ? inputName.slice(CUSTOM_FIELD_NAME_PREFIX.length)
        : inputName;
    // startCase also strips the punctuation that would otherwise be read as
    // polyglot interpolation syntax when this is handed to Locale/translate.
    return startCase(name) || null;
};

// --- Previous-outcome detection ---

// A previous-outcome decision is a single rule whose field is resultsFound. This is
// the sole way to identify one — the config carries no explicit basedOn flag.
export const isPreviousOutcomeRules = config =>
    Array.isArray(config) &&
    config.length === 1 &&
    config[0]?.path?.split('.').pop() === PREVIOUS_OUTCOME_FIELD;

// A switch branches on a previous step's outcome when its config object's path is
// the resultsFound marker (bare, or parents.<id>.resultsFound once wired by
// syncOutcomeDecisionFields). The object shape ({ path, branches }) is what
// distinguishes it from a decision's rules-array previous-outcome config.
export const isPreviousOutcomeSwitch = config =>
    config != null &&
    !Array.isArray(config) &&
    config?.path?.split('.').pop() === PREVIOUS_OUTCOME_FIELD;

// --- Parent node output fields ---

// Build parent field definitions from connected parent nodes.
// Returns { fields: [field, ...], byPath: { path: field } }
export const buildParentFieldDefs = parentNodes => {
    const fields = parentNodes.flatMap(pn => {
        const outputFields = PARENT_OUTPUT_FIELDS[pn.nodeType] || [];
        return outputFields.map(f => ({
            path: `parents.${pn.nodeId}.${f.field}`,
            menuLabel: pn.label,
            operators: f.operators,
            valueType: f.valueType,
            staticOptions: f.staticOptions,
            isParentField: true,
            parentNodeId: pn.nodeId,
        }));
    });

    const byPath = Object.fromEntries(fields.map(f => [f.path, f]));
    return { fields, byPath };
};

// --- Branch value helpers ---

// Flatten a branch's selection into its scalar value(s) — multi-value branches
// hold an array, single-value branches a scalar. Used to keep value choices
// unique across branches: a value picked in one branch is hidden from the others.
export const getBranchValues = branch => {
    if (Array.isArray(branch?.value)) {
        return branch.value;
    }
    return branch?.value == null || branch.value === '' ? [] : [branch.value];
};

// The conditionValue carried on a branch's outgoing edge — its value, which is
// also what the edge chip displays (e.g. "California, US"). Multi-value branches
// join their values. Used to match real children to branches when deriving stubs.
export const getBranchConditionValue = branch =>
    Array.isArray(branch?.value) ? branch.value.join(', ') : String(branch?.value ?? '');

// Display counterpart to getBranchConditionValue: maps each of the branch's raw
// values to its display label.
export const getBranchConditionLabels = (branch, fieldDef, translate) => {
    const options = fieldDef?.staticOptions || [];
    return getBranchValues(branch).map(value => {
        const match = options.find(o => String(o.value) === String(value));
        return translate(match ? match.label : String(value));
    });
};

// A branch is incomplete until its value is filled in (operator defaults to equals).
export const isBlankBranch = branch =>
    Array.isArray(branch.value) ? !branch.value.length : !branch.value;

// Re-point a branch at a new operator, preserving its value where possible.
// - Crossing into/out of an operator with its own option set (e.g. isNull) can't
//   carry the old free-text/select value, so the value resets.
// - Otherwise the value migrates between single ↔ multi shape rather than being
//   dropped: a scalar wraps into a one-item array (empty if blank), and a multi
//   value collapses to its first entry.
export const migrateBranchToOperator = (branch, newOperator) => {
    const oldOpDef = OPERATORS_BY_VALUE[branch.operator];
    const newOpDef = OPERATORS_BY_VALUE[newOperator];

    if (Boolean(oldOpDef?.staticOptions) !== Boolean(newOpDef?.staticOptions)) {
        return { ...branch, operator: newOperator, value: '' };
    }

    let value = branch.value;
    if (Boolean(oldOpDef?.multi) !== Boolean(newOpDef?.multi)) {
        value = newOpDef?.multi
            ? branch.value
                ? [branch.value]
                : []
            : Array.isArray(branch.value)
              ? branch.value[0] || ''
              : branch.value;
    }
    return { ...branch, operator: newOperator, value };
};

// --- Unified Autocomplete option builders ---

// Build unified option shapes for the field Autocomplete: { path, label, group, fieldDef }
export const buildStandardFieldOptions = translate => {
    const options = [];
    const groups = getSortedFieldGroups(translate);
    groups.forEach(group => {
        group.fields.forEach(field => {
            options.push({
                path: field.path,
                label: translate(field.label),
                group: translate(group.label),
                fieldDef: field,
            });
        });
    });
    return options;
};

export const buildParentFieldOptions = (parentNodes, translate) => {
    const { fields } = buildParentFieldDefs(parentNodes);
    return fields.map(f => ({
        path: f.path,
        label: f.menuLabel,
        group: translate(FIELD_GROUPS.PARENT_SEARCH),
        fieldDef: f,
    }));
};

export const buildCustomFieldOption = (fieldData, translate) => ({
    path: `${CUSTOM_FIELD_PATH_PREFIX}${fieldData.inputName}`,
    label: fieldData.label,
    sublabel: fieldData.formName,
    group: translate(FIELD_GROUPS.FORM_FIELDS),
    fieldDef: null,
    fieldData,
});

// Resolve the field definition (operators / valueType / options) for the selected
// path — standard request field, parent-output field, or custom form field.
export const resolveFieldDef = (
    path,
    { parentFieldsByPath = {}, fieldMetadataCache = {} } = {}
) => {
    if (!path) {
        return null;
    }

    const standard = DECISION_FIELDS_BY_PATH[path] || parentFieldsByPath[path];
    if (standard) {
        return standard;
    }

    if (isCustomFieldPath(path)) {
        const cached = fieldMetadataCache[getFieldIdFromPath(path)];
        if (cached) {
            const typeConfig = FORM_FIELD_TYPE_CONFIG[cached.inputType];
            if (typeConfig) {
                return {
                    ...typeConfig,
                    path,
                    label: cached.label,
                    staticOptions:
                        typeConfig.staticOptions ||
                        (Array.isArray(cached.options)
                            ? cached.options.map(o => ({ value: o.value, label: o.label }))
                            : undefined),
                };
            }
        }
    }

    return null;
};

// --- Config <-> form-state formatters ---

/**
 * The node-type formatting layer for AddDecisionDialog.
 *
 * A branching node stores its config in a shape that depends on its type:
 *   - DECISION → a rules array  ([{ path, operator, value }, ...])
 *   - SWITCH   → an object      ({ path, branches: [{ operator, values }] })
 *
 * The dialog, however, edits a single normalized form state regardless of type:
 *   { basedOn, field, branches: [{ operator, value }] }
 *
 * Each formatter adapts between the two: `parseConfig` reads a stored config into
 * form state (input), and `formatValues` writes form state back to the node's
 * config shape (output). `isEditing` / `getEditField` answer the shape-detection
 * questions the dialog needs before it has form state. Keeping this in one place
 * means the dialog never branches on node type itself — it asks the formatter.
 */

const blankBranches = () => [{ ...NEW_BRANCH }];

// DECISION: config is a rules array. A previous-outcome decision is a single rule
// on the `resultsFound` field and renders the fixed succeeded/failed split, so it
// carries no editable field/branches.
const decisionFormatter = {
    isEditing: config => Array.isArray(config),

    getEditField: config => (Array.isArray(config) ? config[0]?.path : undefined),

    parseConfig: config => {
        const editRules = Array.isArray(config) ? config : [];
        const previousOutcome = isPreviousOutcomeRules(editRules);
        return {
            basedOn: previousOutcome ? BRANCH_BASED_ON.PREVIOUS_OUTCOME : BRANCH_BASED_ON.FORM_DATA,
            field: previousOutcome ? '' : editRules[0]?.path || '',
            branches:
                !previousOutcome && editRules.length
                    ? editRules.map(r => ({
                          operator: r.operator || 'equals',
                          value: r.value != null ? r.value : '',
                      }))
                    : blankBranches(),
        };
    },

    formatValues: values =>
        values.basedOn === BRANCH_BASED_ON.PREVIOUS_OUTCOME
            ? [
                  {
                      path: PREVIOUS_OUTCOME_FIELD,
                      operator: 'equals',
                      value: DECISION_OUTCOME_CONDITIONS.SUCCEEDED,
                  },
              ]
            : values.branches
                  .filter(b => !isBlankBranch(b))
                  .map(b => ({
                      path: values.field,
                      operator: b.operator,
                      value: b.value,
                  })),
};

// SWITCH: config is a { path, branches: [{ operator, values }] } object. Each
// branch holds an array of values, unwrapped to a scalar for single-value
// operators in form state. A previous-outcome switch holds the resultsFound marker
// and renders the same fixed succeeded/failed split as a previous-outcome decision.
const switchFormatter = {
    isEditing: config => Boolean(config?.path),

    getEditField: config => config?.path,

    parseConfig: config => {
        const previousOutcome = isPreviousOutcomeSwitch(config);
        const configBranches = Array.isArray(config?.branches) ? config.branches : [];
        const branchRows = configBranches.map(b => {
            const values = Array.isArray(b.values) ? b.values : [];
            return {
                operator: b.operator || 'equals',
                value: OPERATORS_BY_VALUE[b.operator]?.multi ? values : values[0] ?? '',
            };
        });
        return {
            basedOn: previousOutcome ? BRANCH_BASED_ON.PREVIOUS_OUTCOME : BRANCH_BASED_ON.FORM_DATA,
            field: previousOutcome ? '' : config?.path || '',
            branches: !previousOutcome && branchRows.length ? branchRows : blankBranches(),
        };
    },

    formatValues: values => {
        if (values.basedOn === BRANCH_BASED_ON.PREVIOUS_OUTCOME) {
            return {
                path: PREVIOUS_OUTCOME_FIELD,
                branches: [
                    {
                        operator: 'equals',
                        values: [DECISION_OUTCOME_CONDITIONS.SUCCEEDED],
                    },
                ],
            };
        }
        return {
            path: values.field,
            branches: values.branches
                .filter(b => !isBlankBranch(b))
                .map(b => ({ operator: b.operator, values: getBranchValues(b) })),
        };
    },
};

/**
 * Returns the config <-> form-state formatter for a branching node type.
 * Anything that isn't a SWITCH is treated as a DECISION (the rules-array shape).
 *
 * @param {string} nodeType - a NODE_TYPES value
 * @returns {{
 *   isEditing: (config: any) => boolean,
 *   getEditField: (config: any) => (string|undefined),
 *   parseConfig: (config: any) => { basedOn: string, field: string, branches: Array },
 *   formatValues: (values: { basedOn: string, field: string, branches: Array }) => any,
 * }}
 */
export const getDecisionDialogFormatter = nodeType =>
    nodeType === NODE_TYPES.SWITCH ? switchFormatter : decisionFormatter;
